/**
 * Composable that provides helper functions for prospect data.
 * Receives cache refs and returns closured helper functions.
 */
export function useProspectHelpers({
  prospectPreferences,
  prospectAssignments,
  employeesMap,
  teamsMap
}) {
  /**
   * Get assignment for a specific preference
   */
  function getAssignmentForPreference(prospectId, pref) {
    const allAssignments = prospectAssignments.value[prospectId] || []
    const assignments = allAssignments.filter(a => a.is_active !== false)

    // Primary: match via notes marker
    if (pref.id) {
      const byNotes = assignments.find(a => a.notes && a.notes.includes(`pref:${pref.id}`))
      if (byNotes) return byNotes
    }

    // Assignment met een pref-marker voor een ándere rol telt niet als fallback
    const belongsToOtherPref = a => {
      const m = a.notes?.match(/pref:([^\s|]+)/)
      return !!(m && pref.id !== undefined && String(m[1]) !== String(pref.id))
    }

    // Fallback 1: single preference → any legacy assignment matches
    const prefs = prospectPreferences.value[prospectId] || []
    if (prefs.length === 1 && assignments.length > 0 && !belongsToOtherPref(assignments[0])) {
      return assignments[0]
    }

    // Fallback 2: match via PL-selected employee (alleen legacy assignments)
    if (pref.pl_selected_employee_id) {
      const byPl = assignments.find(
        a => a.employee_simplicate_id === pref.pl_selected_employee_id && !belongsToOtherPref(a)
      )
      if (byPl) return byPl
    }

    return undefined
  }

  /**
   * Check if a preference has an assignment
   */
  function hasAssignment(prospectId, pref) {
    return !!getAssignmentForPreference(prospectId, pref)
  }

  /**
   * Check if a prospect is fully assigned
   */
  function isProspectFullyAssigned(prospect) {
    const prefs = prospectPreferences.value[prospect.id] || []
    if (prefs.length === 0) return false
    return prefs.every(pref => !!getAssignmentForPreference(prospect.id, pref))
  }

  /**
   * Check if a prospect is "done" (fully assigned OR action completed)
   */
  function isProspectDone(prospect) {
    if (prospect.action_status === 'completed') return true
    return isProspectFullyAssigned(prospect)
  }

  /**
   * Check if a done prospect should be auto-hidden (completed > 14 days ago)
   */
  function isProspectAutoHidden(prospect) {
    if (prospect.workflow_status === 'te_bespreken') return false

    if (
      prospect.action_type_code === 'kans_afwijzen' &&
      prospect.action_status === 'completed' &&
      prospect.status !== 'OpportunityStatus_open'
    ) {
      return true
    }

    if (!isProspectDone(prospect)) return false
    if (!prospect.action_completed_at) return false
    // Verberg alleen wanneer de Simplicate-kans ook gesloten is — open kansen
    // blijven zichtbaar in Concretiseren ongeacht de leeftijd van de action.
    if (prospect.status === 'OpportunityStatus_open') return false
    const completedAt = new Date(prospect.action_completed_at)
    const fourteenDaysAgo = new Date()
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)
    return completedAt < fourteenDaysAgo
  }

  /**
   * Get unique employee names assigned to a prospect
   */
  function getAssignedEmployeeNames(prospectId) {
    const prefs = prospectPreferences.value[prospectId] || []
    const names = prefs
      .map(pref => getAssignmentForPreference(prospectId, pref))
      .filter(Boolean)
      .map(a => a.employee_name)
      .filter(Boolean)
    return [...new Set(names)]
  }

  /**
   * Pick the most recent assignment (active OR released) for a preference.
   * Used to surface the final Concretiseren choice on prospects whose
   * assignments have been auto-released by a terminal action (project_maken
   * or kans_afwijzen) — see prospect 986 / WijTechniek (2026-05-12).
   */
  function getMostRecentAssignmentForPreference(prospectId, pref) {
    const active = getAssignmentForPreference(prospectId, pref)
    if (active) return active

    const all = prospectAssignments.value[prospectId] || []
    const sortByCreatedDesc = (a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0
      return tb - ta
    }

    if (pref.id) {
      const released = all
        .filter(a => a.is_active === false && a.notes && a.notes.includes(`pref:${pref.id}`))
        .sort(sortByCreatedDesc)
      if (released.length > 0) return released[0]
    }

    if (pref.pl_selected_employee_id) {
      const byPl = all
        .filter(
          a =>
            a.is_active === false &&
            a.employee_simplicate_id === pref.pl_selected_employee_id &&
            !(a.notes && a.notes.match(/pref:([^\s|]+)/) && !a.notes.includes(`pref:${pref.id}`))
        )
        .sort(sortByCreatedDesc)
      if (byPl.length > 0) return byPl[0]
    }

    return undefined
  }

  /**
   * Get unique employee names assigned to a prospect, including the most
   * recent released assignment per role. Use this for display contexts on
   * completed prospects where the historical Concretiseren choice should
   * remain visible.
   */
  function getAssignedEmployeeNamesIncludingHistory(prospectId) {
    const prefs = prospectPreferences.value[prospectId] || []
    const names = prefs
      .map(pref => getMostRecentAssignmentForPreference(prospectId, pref))
      .filter(Boolean)
      .map(a => a.employee_name)
      .filter(Boolean)
    return [...new Set(names)]
  }

  /**
   * Parse preferred employees from preference
   */
  function getPreferredEmployees(pref) {
    if (!pref.preferred_employees) return []
    return Array.isArray(pref.preferred_employees)
      ? pref.preferred_employees
      : JSON.parse(pref.preferred_employees || '[]')
  }

  /**
   * Get team names from team IDs
   */
  function getTeamNames(teamIds) {
    if (!teamIds) return []
    const ids = Array.isArray(teamIds) ? teamIds : JSON.parse(teamIds || '[]')
    return ids.map(id => teamsMap.value[id]).filter(Boolean)
  }

  /**
   * Format experience levels
   */
  function formatExperienceLevels(levels) {
    if (!levels) return []
    return Array.isArray(levels) ? levels : JSON.parse(levels || '[]')
  }

  /**
   * Get employee name by ID
   */
  function getEmployeeName(empId) {
    return employeesMap.value[empId]?.name || empId
  }

  /**
   * Get employee function by ID
   */
  function getEmployeeFunction(empId) {
    return employeesMap.value[empId]?.function || ''
  }

  /**
   * Get employee teams as comma-separated string
   */
  function getEmployeeTeams(empId) {
    const emp = employeesMap.value[empId]
    if (!emp?.teams) return ''
    return emp.teams.map(t => t.name).join(', ')
  }

  /**
   * Get role count for a prospect
   */
  function getRoleCount(prospect) {
    return prospectPreferences.value[prospect.id]?.length || 0
  }

  /**
   * Get assigned count for a prospect
   */
  function getAssignedCount(prospect) {
    const prefs = prospectPreferences.value[prospect.id] || []
    return prefs.filter(pref => hasAssignment(prospect.id, pref)).length
  }

  /**
   * Get assigned employee IDs for a prospect.
   * If `pref` is passed, returns only the employee assigned to that specific role
   * (so an employee can still be assigned to other roles within the same project).
   */
  function getAssignedEmployeeIds(prospectId, pref = null) {
    if (!prospectId) return []
    if (pref) {
      const a = getAssignmentForPreference(prospectId, pref)
      return a ? [a.employee_simplicate_id] : []
    }
    const prefs = prospectPreferences.value[prospectId] || []
    return prefs
      .map(p => getAssignmentForPreference(prospectId, p))
      .filter(Boolean)
      .map(a => a.employee_simplicate_id)
  }

  /**
   * Check if employee is PL recommended
   */
  function isPlRecommended(pref, employeeId) {
    return pref.pl_selected_employee_id === employeeId
  }

  return {
    isProspectFullyAssigned,
    isProspectDone,
    isProspectAutoHidden,
    getAssignedEmployeeNames,
    getAssignedEmployeeNamesIncludingHistory,
    getMostRecentAssignmentForPreference,
    hasAssignment,
    getAssignmentForPreference,
    getPreferredEmployees,
    getTeamNames,
    formatExperienceLevels,
    getEmployeeName,
    getEmployeeFunction,
    getEmployeeTeams,
    getRoleCount,
    getAssignedCount,
    getAssignedEmployeeIds,
    isPlRecommended
  }
}
